/**
 * useCobrancaCalculationMemory
 *
 * Hook responsável por buscar a memória de cálculo de uma cobrança específica
 * via RPC `get_cobranca_calculation_memory`. Read-only — apenas formata o JSON.
 *
 * Uso típico: dentro do modal de detalhes da cobrança, quando o usuário abre
 * o sub-modal "Memória de cálculo" para entender o pro-rata.
 *
 * @author Módulo Financeiro
 * RELEASE: 2026-06-22 v1.1.0 - Tipagem alinhada com a RPC v1.1.0: novos campos em
 *                              cobranca (pro_rata_metadata, tipo, ciclo_origem, narrativa),
 *                              calculo (fator_oficial, factor_source) e novos blocos
 *                              top-level `totais` e `creditos`.
 * RELEASE: 2026-06-17 v1.0.0 - Versão inicial.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export interface CobrancaMemoriaItem {
  id: string;
  descricao: string | null;
  quantidade: number;
  valor_cheio_unitario: number | null;
  pro_rata_factor: number | null;
  valor_unitario: number;
  desconto: number | null;
  total: number;
}

export interface CobrancaMemoriaEvento {
  created_at: string;
  event_type: string;
  event_source: string | null;
  actor_full_name: string | null;
  ciclo_numero: number | null;
  change_diff: any;
  metadata: any;
}

/**
 * Estrutura cru do JSONB `pro_rata_metadata` salvo na cobrança quando ela
 * resulta de um ajuste pro-rata mid-cycle (upgrade/downgrade dentro do ciclo).
 */
export interface CobrancaProRataItemDiff {
  product_id: string | null;
  antigo: {
    id?: string;
    descricao?: string;
    quantidade?: number;
    valor_unitario?: number;
    desconto?: number;
    total?: number;
    product_id?: string;
  } | null;
  novo: {
    id?: string;
    descricao?: string;
    quantidade?: number;
    valor_unitario?: number;
    desconto?: number;
    total?: number;
    product_id?: string;
  } | null;
  credito: number;
  debito: number;
  delta: number;
  skipped?: boolean;
}

export interface CobrancaProRataMetadata {
  factor?: number;
  delta_total?: number;
  mode?: string;
  op_id?: string;
  origem?: string;
  ciclo_origem?: number;
  periodo_inicio?: string;
  periodo_fim?: string;
  items_diff?: CobrancaProRataItemDiff[];
  creditos_consumidos?: number;
  credit_id?: string;
  [k: string]: any;
}

export interface CobrancaMemoriaCredito {
  id: string;
  origem: string;
  valor: number;
  valor_usado: number;
  saldo: number;
  status: string;
  expira_em: string | null;
  assinatura_id: string | null;
  created_at: string;
}

export type CobrancaTipo =
  | 'ciclo_regular'
  | 'pro_rata_criacao'
  | 'mid_cycle_proration'
  | 'cobranca_avulsa';

export interface CobrancaMemoriaCalculo {
  cobranca: {
    id: string;
    ciclo_numero: number | null;
    periodo_inicio: string | null;
    periodo_fim: string | null;
    valor_total: number;
    data_emissao: string | null;
    data_vencimento: string | null;
    status: string;
    descricao: string | null;
    created_at: string;
    // v1.1.0
    is_prorated: boolean | null;
    pro_rata_factor: number | null;
    pro_rata_metadata: CobrancaProRataMetadata | null;
    tipo: CobrancaTipo;
    ciclo_origem: number | null;
    narrativa: string;
  };
  assinatura: {
    id: string;
    frequencia: string;
    ciclos_total: number | null;
    billing_anchor_date: string | null;
    pro_rata_enabled: boolean;
    pro_rata_mode: string | null;
    valor_assinatura: number;
    primeiro_vencimento: string | null;
  } | null;
  tenant_config: {
    enabled?: boolean;
    mode?: string;
    apply_on_create?: boolean;
    apply_on_plan_change?: boolean;
    rounding?: string;
    [k: string]: any;
  };
  itens: CobrancaMemoriaItem[];
  calculo: {
    dias_periodo: number | null;
    dias_cobrados: number | null;
    fator_efetivo: number | null;
    soma_itens: number;
    soma_descontos: number;
    total_calculado: number;
    divergencia_vs_persistido: number;
    // v1.1.0
    fator_oficial: number | null;
    factor_source: 'pro_rata_metadata' | 'cobranca.pro_rata_factor' | 'avg_itens';
  };
  // v1.1.0 — blocos novos
  totais: {
    subtotal_itens: number;
    descontos: number;
    credito_aplicado: number;
    total_cobrado: number;
    delta_total: number | null;
  };
  creditos: {
    saldo_disponivel_atual: number;
    lista_disponivel: CobrancaMemoriaCredito[];
  };
  eventos: CobrancaMemoriaEvento[];
}

export function useCobrancaCalculationMemory(
  cobrancaId: string | null | undefined,
  enabled: boolean = true,
) {
  return useQuery<CobrancaMemoriaCalculo>({
    queryKey: ['cobranca-calculation-memory', cobrancaId],
    enabled: !!cobrancaId && enabled,
    staleTime: 30_000,
    queryFn: async () => {
      // Chama a RPC segura que valida tenant e retorna a memória de cálculo
      const { data, error } = await supabase.rpc('get_cobranca_calculation_memory', {
        p_cobranca_id: cobrancaId as string,
      });

      if (error) {
        logger.error('useCobrancaCalculationMemory', 'Falha ao carregar memória', error);
        throw error;
      }

      return data as unknown as CobrancaMemoriaCalculo;
    },
  });
}
